import { useEffect, useState } from "react";
import { Button, Card, Col, FloatingLabel, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import AlertBox from "../../../components/Messages";
import { useCountryArr } from "../../../hooks/useCountryArr";
import { useGetProfileQuery, useUpdateProfileMutation } from "../../../services/API/ProfileAPI";
import {
  collapseWhitespace,
  formatBlockedChars,
  getDisallowedChars,
  stripDisallowedChars,
} from "../../../utils/latinCharset";
import "./Profile.scss";

function Profile() {
  const { t } = useTranslation(["translation", "account", "register"]);
  const countryArr = useCountryArr();

  const { data: profileData } = useGetProfileQuery();
  const [updateUser] = useUpdateProfileMutation();

  const initialState = {
    username: "",
    name: "",
    surname: "",
    email: "",
    phone: "",
    company_name: "",
    city: "",
    address: "",
    zipcode: "",
    country: "",
    salutation: "",
    title: "",
  };
  const [formState, setFormState] = useState(initialState);
  const [isSuccess, setIsSuccess] = useState(false);
  // Which field last had disallowed characters removed, and which ones.
  const [blocked, setBlocked] = useState({ field: null, chars: [] });

  const handleChange = ({ target: { name, value } }) => {
    setIsSuccess(false);
    // Restrict to Latin characters, same rule as the registration form.
    const cleaned = collapseWhitespace(stripDisallowedChars(value));
    const removed = getDisallowedChars(value);
    setBlocked(removed.length ? { field: name, chars: removed } : { field: null, chars: [] });
    return setFormState((prev) => ({ ...prev, [name]: cleaned }));
  };

  // Renders a hint below the given field naming the characters that were
  // rejected, if the last blocked input happened in that field.
  const charHint = (field) =>
    blocked.field === field && blocked.chars.length > 0 ? (
      <div className="small text-danger mt-1">
        {t("error.charSet", { ns: "register", chars: formatBlockedChars(blocked.chars) })}
      </div>
    ) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    await updateUser({ ...formState })
      .then(async (result) => {
        setIsSuccess(true);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  useEffect(() => {
    setFormState({ ...profileData });
  }, [profileData]);

  return (
    <Card className="mb-4">
      <Card.Header>{t("profileDetails", { ns: "account" })}</Card.Header>
      <Card.Body>
        <Form>
          <Form.Group className="mb-3" controlId="formBasicUsername">
            <Form.Label>{t("userList.tableHeader.username")}</Form.Label>
            <Form.Control
              name="username"
              type="text"
              onChange={handleChange}
              value={formState?.username ?? ""}
              disabled
            />
            <Form.Text className="text-muted">{t("userList.editModal.usernameText")}</Form.Text>
          </Form.Group>

          <Row>
            <Col md={6}>
              <FloatingLabel
                className="mb-md-3"
                controlId="InputSalutation"
                label={t("labelText.salutation", {
                  ns: "register",
                })}
              >
                <Form.Select
                  name="salutation"
                  value={formState?.salutation ?? ""}
                  onChange={handleChange}
                  required
                >
                  <option key={1} value={"male"}>
                    {t("salutation.male", {
                      ns: "register",
                    })}
                  </option>
                  <option key={2} value={"female"}>
                    {t("salutation.female", {
                      ns: "register",
                    })}
                  </option>
                  <option key={3} value={"diverse"}>
                    {t("salutation.diverse", {
                      ns: "register",
                    })}
                  </option>
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col md={6}>
              <FloatingLabel
                className="mb-3"
                controlId="InputTitle"
                label={t("labelText.title", {
                  ns: "register",
                })}
              >
                <Form.Control
                  onChange={handleChange}
                  value={formState?.title ?? ""}
                  type="text"
                  placeholder={t("labelText.title", {
                    ns: "register",
                  })}
                  name="title"
                  required
                />
              </FloatingLabel>
              {charHint("title")}
            </Col>
          </Row>

          <FloatingLabel
            className="mb-3"
            controlId="formBasicName"
            label={t("userList.tableHeader.name")}
          >
            <Form.Control
              name="name"
              placeholder={t("userList.tableHeader.name")}
              type="text"
              onChange={handleChange}
              value={formState?.name ?? ""}
            />
          </FloatingLabel>
          {charHint("name")}

          <FloatingLabel
            className="mb-3"
            controlId="formBasicSurname"
            label={t("userList.tableHeader.surname")}
          >
            <Form.Control
              name="surname"
              placeholder={t("userList.tableHeader.surname")}
              type="text"
              onChange={handleChange}
              value={formState?.surname ?? ""}
            />
          </FloatingLabel>
          {charHint("surname")}

          <FloatingLabel
            className="mb-3"
            controlId="formBasicEmail"
            label={t("userList.tableHeader.email")}
          >
            <Form.Control
              name="email"
              placeholder={t("userList.tableHeader.email")}
              type="text"
              onChange={handleChange}
              value={formState?.email ?? ""}
            />
          </FloatingLabel>
          {charHint("email")}

          <FloatingLabel
            className="mb-3"
            controlId="formBasicPhone"
            label={t("userList.tableHeader.phone")}
          >
            <Form.Control
              name="phone"
              placeholder={t("userList.tableHeader.phone")}
              type="text"
              onChange={handleChange}
              value={formState?.phone ?? ""}
            />
          </FloatingLabel>
          {charHint("phone")}

          <FloatingLabel
            className="mb-3"
            controlId="formBasicCompanyName"
            label={t("userList.tableHeader.company")}
          >
            <Form.Control
              name="company_name"
              placeholder={t("userList.tableHeader.company")}
              type="text"
              onChange={handleChange}
              value={formState?.company_name ?? ""}
            />
          </FloatingLabel>
          {charHint("company_name")}

          <FloatingLabel
            className="mb-3"
            controlId="formBasicCity"
            label={t("userList.tableHeader.city")}
          >
            <Form.Control
              name="city"
              placeholder={t("userList.tableHeader.city")}
              type="text"
              onChange={handleChange}
              value={formState?.city ?? ""}
            />
          </FloatingLabel>
          {charHint("city")}

          <FloatingLabel
            className="mb-3"
            controlId="formBasicAddress"
            label={t("userList.tableHeader.address")}
          >
            <Form.Control
              name="address"
              placeholder={t("userList.tableHeader.address")}
              type="text"
              onChange={handleChange}
              value={formState?.address ?? ""}
            />
          </FloatingLabel>
          {charHint("address")}

          <FloatingLabel
            className="mb-3"
            controlId="formBasicZipCode"
            label={t("userList.tableHeader.zipcode")}
          >
            <Form.Control
              name="zipcode"
              placeholder={t("userList.tableHeader.zipcode")}
              type="text"
              onChange={handleChange}
              value={formState?.zipcode ?? ""}
            />
          </FloatingLabel>
          {charHint("zipcode")}

          <FloatingLabel
            className="mb-3"
            controlId="formBasicCountry"
            label={t("userList.tableHeader.country")}
          >
            <Form.Select
              name="country"
              value={formState.country}
              onChange={handleChange}
              className="register-form-control"
            >
              {!!countryArr?.length &&
                countryArr.map(({ label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
            </Form.Select>
          </FloatingLabel>

          <Button variant="primary" type="submit" onClick={handleSubmit}>
            {t("submit", { ns: "account" })}
          </Button>

          <AlertBox
            show={isSuccess}
            message={t("dataUpdate", { ns: "account" })}
            customVariant={"success"}
          />
        </Form>
      </Card.Body>
    </Card>
  );
}

export default Profile;
